/**
 * The database, as the engine sees it.
 *
 * These four functions are the export surface `apps/mobile-pwa/src/lib/db/sqlite.ts`
 * had before the engine moved out of the app, kept byte-for-byte so that
 * `migrations.ts`, `queue.ts`, `pull.ts` and `versions.ts` needed no edits at
 * their call sites — only a change of import specifier. Preserving that shape
 * is what made the extraction a move rather than a rewrite, and it is why the
 * suites that pin those files could come along unchanged.
 *
 * Each call reads the registry rather than capturing a driver at module scope,
 * so a test that installs a fresh driver between cases gets it.
 */
import { platform, type DbInfo, type Statement } from '@kapture/platform';

export type { DbInfo, Statement };

/** Open the database and report what is backing it. */
export function openDatabase(): Promise<DbInfo> {
  return platform().db.openDatabase();
}

/** Run a one-shot statement. Accepts multi-statement SQL when unbound. */
export function run(sql: string, bind?: unknown[]): Promise<void> {
  return platform().db.run(sql, bind);
}

/** Run a query and return rows as plain objects. */
export function all<T = Record<string, unknown>>(
  sql: string,
  bind?: unknown[],
): Promise<T[]> {
  return platform().db.all<T>(sql, bind);
}

/** Run statements in one transaction: all of them, or none. */
export function tx(statements: Statement[]): Promise<void> {
  return platform().db.tx(statements);
}
